/**
 * NBT cart savings presentation helpers.
 *
 * Don Price / dj-giardco
 *
 * Integration boundary:
 * - Read-only calculations from Platter's existing Alpine cart store.
 * - Does not add, remove, apply, or recalculate Shopify discounts.
 * - Does not change bundle, gift-with-purchase, upsell, quantity, or checkout behavior.
 * - All currency values stay in Shopify's integer minor units (cents).
 */
(() => {
  "use strict";

  // Don Price / dj-giardco: Avoid replacing an existing helper if Theme Editor
  // hot reload evaluates this asset more than once.
  if (window.djGiardcoCartSavings) return;

  const toNumber = (value, fallback = 0) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
  };

  const cleanText = (value, fallback = "") => {
    const text = String(value ?? "").trim();
    return text || fallback;
  };

  const normalizeDiscountApplication = (allocation) =>
    allocation?.discount_application ?? allocation ?? {};

  const getCartState = (cartStore) => cartStore?.state ?? {};

  const getMerchandiseTotal = (cartState) => {
    const calculatedOriginalTotal = (cartState?.items ?? []).reduce(
      (total, item) =>
        total +
        toNumber(item?.quantity) *
          toNumber(item?.selling_plan_allocation?.compare_at_price ?? item?.original_price),
      0,
    );

    return Math.max(
      0,
      toNumber(
        cartState?.original_pre_selling_plan_total_price ??
          cartState?.original_total_price ??
          cartState?.items_subtotal_price,
        calculatedOriginalTotal,
      ),
    );
  };

  const getSubtotal = (cartState) => Math.max(0, toNumber(cartState?.total_price));

  // NBT: sale prices are crossed-out (compare-at) prices, which /cart.js doesn't include.
  // Fetch each product in the cart once and remember its variants' compare-at prices, so
  // "Item total" is the full original value and "You save" is original value minus what you pay.
  const compareCache = {};
  const compareLoading = {};
  const loadCompareAt = (cartState, onLoaded) => {
    (cartState?.items ?? []).forEach((item) => {
      const handle = item?.handle;
      if (!handle || compareLoading[handle]) return;
      compareLoading[handle] = fetch(`${window.Shopify?.routes?.root || "/"}products/${handle}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .then((product) => {
          (product?.variants ?? []).forEach((variant) => {
            compareCache[variant.id] = toNumber(variant.compare_at_price);
          });
          onLoaded();
        })
        .catch(() => {});
    });
  };
  const getOriginalValue = (cartState) =>
    (cartState?.items ?? []).reduce(
      (total, item) =>
        total +
        toNumber(item?.quantity) *
          Math.max(toNumber(item?.original_price), compareCache[item?.variant_id] || 0),
      0,
    );

  const getSavings = (cartState) =>
    Math.max(0, getMerchandiseTotal(cartState) - getSubtotal(cartState));

  const getDiscountApplications = (cartState) => {
    const cartApplications = (cartState?.cart_level_discount_applications ?? []).map(
      normalizeDiscountApplication,
    );
    const lineApplications = (cartState?.items ?? []).flatMap((item) =>
      (item?.line_level_discount_allocations ?? []).map(normalizeDiscountApplication),
    );

    return [...cartApplications, ...lineApplications].filter(Boolean);
  };

  const getDiscountPercentage = (discountApplication) => {
    if (discountApplication?.value_type === "percentage") {
      return Math.max(0, toNumber(discountApplication?.value));
    }

    // Don Price / dj-giardco: Some Shopify Functions expose the percentage
    // only in the customer-facing title. This is a display fallback only.
    const titlePercentage = cleanText(discountApplication?.title).match(/(\d+(?:\.\d+)?)\s*%/);
    return titlePercentage ? Math.max(0, toNumber(titlePercentage[1])) : 0;
  };

  // Don Price / dj-giardco: Match server data to the settled native cart so a
  // slow response cannot replace a newer quantity/discount result. Item count
  // is deliberately omitted: Platter hides child lines from its item_count.
  const cartSignature = (cart) => JSON.stringify([
    cart?.currency?.iso_code ?? cart?.currency,
    cart?.total_price, cart?.original_total_price,
    (cart?.items ?? []).map(i => [i.key, i.quantity, i.original_line_price, i.final_line_price]),
  ]);
  const entryRequests = new Map();
  const fetchEntries = (signature) => {
    const existing = entryRequests.get(signature);
    if (existing && Date.now() - existing.time < 3000) return existing.promise;
    const request = { time: Date.now(), promise: null };
    request.promise = (async () => {
      for (let attempt = 0; attempt < 2; attempt++) {
        const url = new URL(`${window.Shopify?.routes?.root ?? '/'}cart`, window.location.origin);
        url.searchParams.set('section_id', 'dj-cart-entry-data');
        url.searchParams.set('_dj', String(Date.now()));
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(url, {credentials:'same-origin', cache:'no-store', signal:controller.signal});
          if (!response.ok) throw new Error('Entry data unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const data = JSON.parse(html.querySelector('[data-dj-cart-entry-data]')?.textContent ?? 'null');
          if (!data?.enabled) throw new Error('Entry campaign disabled');
          if (cartSignature(data) === signature) return calculateEntries(data);
        } finally { clearTimeout(timeout); }
      }
      throw new Error('Cart changed during entry calculation');
    })().catch(error => { if (entryRequests.get(signature) === request) entryRequests.delete(signature); throw error; });
    entryRequests.set(signature, request);
    if (entryRequests.size > 8) entryRequests.delete(entryRequests.keys().next().value);
    return request.promise;
  };

  // Don Price / dj-giardco: Exact rational arithmetic rounds down ONCE after
  // adding lines. Paid children are included once as native cart lines. Missing,
  // non-numeric or negative metafields contribute zero. Never infer from price.
  const calculateEntries = (data) => {
    const gcd = (a,b) => { while (b) [a,b]=[b,a%b]; return a; };
    let numerator=0n, denominator=1n, paidTotal=0;
    for (const item of data.items ?? []) {
      const original=toNumber(item.original_line_price), paid=toNumber(item.paid_line_price);
      if (![original,paid,item.quantity].every(Number.isSafeInteger) || paid<0 || paid>original) throw new Error('Invalid entry price data');
      paidTotal+=paid;
      if (item.excluded || paid<=0 || original<=0 || item.quantity<=0) continue;
      const base=String(item.base_entries ?? '').trim();
      if (!/^\d+(?:\.\d{1,6})?$/.test(base)) continue;
      const [whole,decimal='']=base.split('.');
      const value=BigInt(whole+decimal), scale=10n**BigInt(decimal.length);
      const n=value*BigInt(item.quantity)*BigInt(paid), d=scale*BigInt(original);
      numerator=numerator*d+n*denominator; denominator*=d;
      const factor=gcd(numerator,denominator); numerator/=factor; denominator/=factor;
    }
    // Fail closed if Shopify allocations do not reconcile to the cart total.
    // No proportional guessing for order-level discounts in the real theme.
    if (paidTotal!==data.total_price) throw new Error('Discount allocations do not reconcile');
    const count=numerator/denominator;
    if (count>BigInt(Number.MAX_SAFE_INTEGER)) throw new Error('Entry count exceeds display limit');
    return Number(count);
  };

  const readConfiguration = (element) => {
    const dataset = element?.dataset ?? {};
    const tiers = [
      {
        target: toNumber(dataset.tierOneTarget, 20000),
        percent: toNumber(dataset.tierOnePercent, 10),
      },
      {
        target: toNumber(dataset.tierTwoTarget, 30000),
        percent: toNumber(dataset.tierTwoPercent, 20),
      },
      {
        target: toNumber(dataset.tierThreeTarget, 40000),
        percent: toNumber(dataset.tierThreePercent, 30),
      },
    ]
      .filter((tier) => tier.target > 0 && tier.percent > 0)
      .sort((tierA, tierB) => tierA.target - tierB.target);

    return {
      tiers,
      tiersEnabled: dataset.tiersEnabled === 'true',
      tierKeyword: cleanText(dataset.tierKeyword).toLowerCase(),
      giveawayEnabled: dataset.giveawayEnabled === 'true' && dataset.showEntries === 'true',
      designMode: dataset.designMode === 'true',
      // Cleared in the theme editor means cleared. Only an absent attribute
      // falls back, so emptying a field actually removes the line.
      entryNote: cleanText(dataset.entryNote),
      entryHeadline: cleanText(dataset.entryHeadline),
      entriesSummaryLabel: cleanText(dataset.entriesSummaryLabel),
      entryCallout: cleanText(dataset.entryCallout),
      giftEnabled: dataset.giftEnabled === 'true',
      giftTarget: Math.max(0,toNumber(dataset.giftTarget)),
      giftProductId: toNumber(dataset.giftProductId),
      giftImage: cleanText(dataset.giftImage),
      giftValue: Math.max(0,toNumber(dataset.giftValue)),
      giftRemainingText: cleanText(dataset.giftRemaining),
      giftReachedText: cleanText(dataset.giftReached),
      shippingAlwaysText: cleanText(dataset.shippingAlways),
      shippingReachedText: cleanText(dataset.shippingReached),
      shippingRemainingText: cleanText(dataset.shippingRemaining),
      shippingNote: cleanText(dataset.shippingNote),
      showSavings: dataset.showSavings === 'true',
      shippingEnabled: dataset.shippingEnabled === 'true' && dataset.showShipping === 'true',
      shippingMode: dataset.shippingMode,
      shippingTarget: Math.max(0,toNumber(dataset.shippingTarget)),
      shippingBasis: dataset.shippingBasis,
      shippingCountries: cleanText(dataset.shippingCountries).split(',').map(s=>s.trim().toUpperCase()).filter(Boolean),
      country: dataset.country,
      campaignCurrency: dataset.campaignCurrency,
      currency: dataset.currentCurrency,
      progressMode: dataset.progressMode || 'auto',
      promotionKeyword: cleanText(dataset.promotionKeyword, "Anniversary"),
      promotionLabel: cleanText(dataset.promotionLabel, "Anniversary savings"),
      fallbackSavingsLabel: cleanText(dataset.fallbackSavingsLabel, "Applied savings"),
    };
  };

  window.djGiardcoCartSavings = {
    calculateEntries,
    cartSignature,
    /**
     * Don Price / dj-giardco: Returns Alpine-friendly computed properties.
     * Keeping the cart store reference intact lets Alpine update the display
     * after every native cart mutation without adding a second listener.
     */
    create(element, cartStore) {
      const configuration = readConfiguration(element);
      let requestedSignature=null, generation=0, destroyed=false;

      return {
        cartStore,
        configuration,
        compareVersion: 0,
        entriesCount: null,
        entriesState: 'loading',
        entriesForSignature: null,
        get entrySignature() { return cartSignature(this.cartState); },
        get cartBusy() { return !!(this.cartStore?.loading || this.cartStore?.isChanging || Object.keys(this.cartStore?.debounce_updates ?? {}).length); },
        syncEntries(signature, busy, force=false) {
          if (!configuration.giveawayEnabled) return;
          if (busy) { requestedSignature=null; generation++; this.entriesState='loading'; return; }
          if (!force && signature===requestedSignature) return;
          requestedSignature=signature;
          const version=++generation;
          this.entriesState='loading';
          if (!(this.cartState.items ?? []).length) { this.entriesCount=0; this.entriesForSignature=signature; this.entriesState='ready'; return; }
          fetchEntries(signature).then(count=>{
            if (destroyed || generation!==version) return;
            this.entriesCount=count; this.entriesForSignature=signature; this.entriesState='ready';
          }).catch(()=>{
            if (destroyed || generation!==version) return;
            // Don Price / dj-giardco: In the theme editor the entry-data request
            // carries no draft-theme context, so it cannot resolve there. Show a
            // sample count in the editor only, so the copy can still be reviewed.
            if (configuration.designMode) { this.entriesCount=12; this.entriesForSignature=signature; this.entriesState='ready'; return; }
            this.entriesState='error';
          });
        },
        destroy() { destroyed=true; generation++; },
        // Don Price / dj-giardco: A retry refreshes both existing blocks so the
        // top message and summary cannot remain in different error states.
        retryEntries() { window.dispatchEvent(new Event('dj-cart-entries-retry')); },
        get entriesReady() { return !this.cartBusy && this.entriesState==='ready' && this.entriesForSignature===this.entrySignature; },
        get entriesDisplay() { return this.entriesReady ? new Intl.NumberFormat(document.documentElement.lang || 'en').format(this.entriesCount) : this.entriesState==='error'?'Unavailable':'Updating…'; },
        get entriesHeadline() { if(!this.entriesReady) return this.entriesState==='error'?'Entries temporarily unavailable':'Updating giveaway entries…'; return this.configuration.entryHeadline.replace(/\[count\]/gi, this.entriesDisplay).replace(/\[entries\]/gi, this.entriesCount===1?'entry':'entries'); },
        get campaignCurrencyMatches() { return this.configuration.currency===this.configuration.campaignCurrency; },
        get tiersActive() { return this.configuration.tiersEnabled && this.campaignCurrencyMatches; },
        get showSavings() { return this.configuration.showSavings && (this.savings>0 || this.tiersActive); },
        get showEntries() { return this.configuration.giveawayEnabled; },
        get showShipping() { return this.configuration.shippingEnabled && this.campaignCurrencyMatches && (!this.configuration.shippingCountries.length || this.configuration.shippingCountries.includes(this.configuration.country)) && (this.configuration.shippingMode==='always' || this.configuration.shippingTarget>0); },
        get showStatus() { return this.showSavings || this.showEntries || this.showShipping || this.showGift; },
        // NBT gift with purchase: display only, mirrors the shipping line. The gift's own
        // lines are left out of the spend so it can't unlock itself.
        get showGift() { return this.configuration.giftEnabled && this.campaignCurrencyMatches && this.configuration.giftTarget>0; },
        get giftSpend() { const id=this.configuration.giftProductId; const own=id?(this.cartState.items??[]).filter(i=>toNumber(i.product_id)===id).reduce((t,i)=>t+toNumber(i.final_line_price),0):0; return Math.max(0,this.subtotal-own); },
        get giftReached() { return this.giftSpend>=this.configuration.giftTarget; },
        get giftRemaining() { return Math.max(0,this.configuration.giftTarget-this.giftSpend); },
        get giftProgress() { return this.configuration.giftTarget?Math.min(100,Math.max(0,this.giftSpend/this.configuration.giftTarget*100)):0; },
        get giftLine() {
          const raw=this.giftReached?this.configuration.giftReachedText:this.configuration.giftRemainingText;
          if (!raw) return '';
          const box=document.createElement('div'); box.textContent=raw;
          const line=(box.innerHTML.replace(/\[amount\]/gi, (window.utils?.formatMoney ? window.utils.formatMoney(this.giftRemaining) : '$'+(this.giftRemaining/100).toFixed(2))));
          // NBT: FREE only turns red once the gift is unlocked.
          return this.giftReached ? this.redFree(line) : line;
        },
        // NBT: the all-caps word FREE turns red in the gift and shipping lines.
        redFree(html) {
          return (html || '').replace(/\bFREE\b/g, '<span class="dj-cart-gift__free">FREE</span>');
        },
        get shippingSpend() { return this.configuration.shippingBasis==='before'?toNumber(this.cartState.original_total_price):this.subtotal; },
        get shippingReached() { return this.configuration.shippingMode==='always' || this.shippingSpend>=this.configuration.shippingTarget; },
        get shippingRemaining() { return Math.max(0,this.configuration.shippingTarget-this.shippingSpend); },
        get shippingProgress() { return this.configuration.shippingTarget?Math.min(100,Math.max(0,this.shippingSpend/this.configuration.shippingTarget*100)):0; },
        get showTierProgress() { return this.showSavings && this.tiersActive && ['auto','tiers'].includes(this.configuration.progressMode); },
        get showShippingProgress() { return this.showShipping && this.configuration.shippingMode!=='always' && (this.configuration.progressMode==='shipping' || this.configuration.progressMode==='auto'&&!this.showTierProgress); },

        get cartState() {
          return getCartState(this.cartStore);
        },

        get merchandiseTotal() {
          // NBT: reading compareVersion makes Alpine re-run this once compare-at prices arrive.
          this.compareVersion;
          loadCompareAt(this.cartState, () => { this.compareVersion++; });
          return Math.max(getMerchandiseTotal(this.cartState), getOriginalValue(this.cartState));
        },

        get subtotal() {
          return getSubtotal(this.cartState);
        },

        get savings() {
          return Math.max(0, this.merchandiseTotal - this.subtotal);
        },

        get discountApplications() {
          return getDiscountApplications(this.cartState);
        },

        get promotionDiscountApplications() {
          const promotionKeyword = this.configuration.tierKeyword;
          if (!this.tiersActive || !promotionKeyword) return [];

          return this.discountApplications.filter((discountApplication) =>
            cleanText(discountApplication?.title).toLowerCase().includes(promotionKeyword),
          );
        },

        get hasNonPromotionDiscounts() {
          const promotionKeyword = this.configuration.promotionKeyword.toLowerCase();

          return this.discountApplications.some(
            (discountApplication) =>
              !cleanText(discountApplication?.title).toLowerCase().includes(promotionKeyword),
          );
        },

        get appliedPromotionPercentage() {
          return this.promotionDiscountApplications.reduce(
            (highestPercentage, discountApplication) =>
              Math.max(highestPercentage, getDiscountPercentage(discountApplication)),
            0,
          );
        },

        get qualifiedTier() {
          if (!this.tiersActive) return null;
          return (
            [...this.configuration.tiers]
              .reverse()
              .find((tier) => this.merchandiseTotal >= tier.target) ?? null
          );
        },

        get nextTier() {
          if (!this.tiersActive) return null;
          return (
            this.configuration.tiers.find((tier) => this.merchandiseTotal < tier.target) ?? null
          );
        },

        get topTier() {
          return this.configuration.tiers[this.configuration.tiers.length - 1] ?? null;
        },

        get currentTierPercentage() {
          if (this.savings <= 0) return 0;

          // Don Price / dj-giardco: A free gift can legitimately carry a 100%
          // line discount. Only the configured promotion may label a 10/20/30
          // tier as unlocked, so unrelated gifts never override this badge.
          return this.appliedPromotionPercentage || 0;
        },

        get amountToNextTier() {
          return this.nextTier ? Math.max(0, this.nextTier.target - this.merchandiseTotal) : 0;
        },

        get progressPercentage() {
          if (!this.topTier?.target) return 0;
          if (this.savings > 0 && this.currentTierPercentage <= 0) return 0;
          return Math.min(100, Math.max(0, (this.merchandiseTotal / this.topTier.target) * 100));
        },

        get badgeText() {
          if (this.savings > 0 && this.currentTierPercentage > 0) {
            return `${this.currentTierPercentage}% OFF UNLOCKED`;
          }

          if (this.savings > 0) return "SAVINGS APPLIED";
          return this.nextTier ? `NEXT: ${this.nextTier.percent}% OFF` : "BEST PRICE";
        },

        get kickerText() {
          if (
            this.savings > 0 &&
            this.topTier &&
            this.currentTierPercentage >= this.topTier.percent
          ) {
            return "TOP TIER";
          }

          if (this.savings > 0 && this.currentTierPercentage > 0) {
            return `${this.currentTierPercentage}% TIER`;
          }

          return this.savings > 0 ? "CART SAVINGS" : "SAVINGS STATUS";
        },

        get supportText() {
          if (this.savings > 0 && this.currentTierPercentage > 0) {
            return "Best price applied automatically. No code needed.";
          }
          if (this.savings > 0) return "Applied offers are reflected in your subtotal.";
          if (this.qualifiedTier) return "Your qualifying price is updating automatically.";
          return "Your cart updates automatically. No code needed.";
        },

        get savingsLabel() {
          const matchingPromotion = this.discountApplications.find((discountApplication) =>
            cleanText(discountApplication?.title)
              .toLowerCase()
              .includes(this.configuration.promotionKeyword.toLowerCase()),
          );
          const percentageSuffix = matchingPromotion && this.appliedPromotionPercentage
            ? ` (${this.appliedPromotionPercentage}%)`
            : "";

          if (matchingPromotion && this.hasNonPromotionDiscounts) {
            const tierSuffix = this.appliedPromotionPercentage
              ? ` (${this.appliedPromotionPercentage}% tier)`
              : "";

            return `Total savings${tierSuffix}`;
          }

          return matchingPromotion
            ? `${this.configuration.promotionLabel}${percentageSuffix}`
            : `${this.configuration.fallbackSavingsLabel}${percentageSuffix}`;
        },

        isTierQualified(target) {
          const tier = this.configuration.tiers.find(
            (configuredTier) => configuredTier.target === toNumber(target),
          );

          // Don Price / dj-giardco: Once savings exist, checkmarks represent
          // the configured promotion actually unlocked, not spend alone. This
          // prevents excluded products or unrelated offers from showing false
          // 10/20/30 completion states.
          if (this.savings > 0) {
            return !!tier && this.currentTierPercentage >= tier.percent;
          }

          return false;
        },
      };
    },
  };
})();
